import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { PdfToAudioDemoPage } from '@/components/astro/new-design-25/SEO/pdf-to-audio-demo-page';
import { pdfToAudioSearch } from '@/lib/pdf-to-audio-search';
import { hreflangLinks, seo } from '@/seo/seo';

const heroTitle = 'French PDF to audio';
const heroDescription =
  'Drop a French PDF and hear it read aloud with natural Parisian or Canadian French voices. Built for academic papers, literature, and professional French content.';

const featureFeatures = [
  {
    title: 'Parisian and Canadian Variants',
    description:
      'Switch between metropolitan French and Québécois voices to match your French-language source material.',
  },
  {
    title: 'Liaisons and Elisions Done Right',
    description:
      'Reads French with natural enchaînements, liaisons, and dropped vowels, not the broken word-by-word delivery of generic tools.',
  },
  {
    title: 'Academic French Vocabulary',
    description:
      'Trained on serious French prose: philosophy, literary criticism, scientific research, and technical writing.',
  },
  {
    title: 'Right for Language Learners',
    description:
      'Slow the playback for shadowing, then speed up as your French ear improves on real native content.',
  },
];

const solutionBenefitsData = {
  title: 'Why francophone readers keep coming back',
  items: [
    {
      text: 'Natural Parisian and Québécois voices, picked per project so the register fits the audience',
      highlight: 'Parisian and Québécois voices',
    },
    {
      text: 'Correct handling of accents, cedillas, and liaisons that generic French TTS mangles',
      highlight: 'accents, cedillas, and liaisons',
    },
    {
      text: 'Clean pronunciation of French academic terminology, philosophical vocabulary, and proper nouns',
      highlight: 'academic terminology',
    },
    {
      text: 'Adjustable speed from 0.5x to 4x for shadowing practice and long-form listening',
      highlight: '0.5x to 4x',
    },
  ],
};

const useCasesData = {
  title: 'How French PDF to audio gets used',
  description:
    'From students at the Sorbonne to Quebec researchers and French learners abroad, here is who uses this tool.',
  useCases: [
    {
      title: 'University Student in France',
      challenge:
        'Work through dense French theoretical texts in literature, philosophy, sociology, or political science.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Drop course PDFs, listen on the way to class with natural French, adjustable speed for hard passages.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Robotic delivery turns Foucault, Bourdieu, or Levinas into nonsense, and you stop using it after a chapter.',
        },
      ],
    },
    {
      title: 'French Learner Building Listening Skill',
      challenge:
        'Get past textbook audio and into real French PDFs at a pace you can actually follow.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Drop any French PDF, slow it to 0.7x, and shadow native pronunciation with synced highlighting in the app.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Wooden phrasing teaches the wrong rhythm and makes real native French even harder to understand later.',
        },
      ],
    },
    {
      title: 'Québécois Researcher or Educator',
      challenge:
        'Produce French audio content for Québec and Canadian francophone audiences, not just metropolitan French.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Pick a Canadian French voice so course materials and research summaries sound right to a Québécois ear.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'One generic European-only voice that signals immediately the content was not made for a Canadian audience.',
        },
      ],
    },
    {
      title: 'Professional Producing French Content',
      challenge:
        'Turn French newsletters, internal memos, or reports into audio that is comfortable to listen to.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Generate natural French read-alongs on demand, with the same voices across web, mobile, and the Chrome extension.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Audio that feels unprofessional and clearly machine-made undermines the credibility of the content itself.',
        },
      ],
    },
  ],
};

const seoSections = [
  {
    title: 'Why French PDFs Are Harder Than They Look',
    content:
      'French is harder for TTS than English. Liaisons connect words across boundaries, vowels elide together, silent consonants come alive depending on what follows, and accents change pronunciation in ways that change meaning. A generic French voice that reads word by word, leaving liaisons out and dropping the music of the language, makes everything sound foreign and amateurish, even to a non-native ear.',
    contentSecondary:
      'Listening was built for full paragraphs of real French, not for marketing taglines. Our voices have been trained on academic, literary, and journalistic French content. We support metropolitan French (the voice you hear on Radio France) and Canadian French (the voice you hear in Québec media). The result is PDF audio you can leave running for an hour without feeling the urge to turn it off.',
  },
  {
    title: 'French PDF to Audio for Study, Work, and Language Learning',
    content:
      'French learners get more out of native input than from grammar drills, but native input is hard to find in formats you can control. Slowing real French audio down usually distorts it. Speeding it up usually mangles it. Our French PDF tool lets you take any French PDF you have — a chapter, a paper, a story — and turn it into clean audio at any speed.',
    contentSecondary:
      'For French-speaking students and researchers, it solves a different problem: keeping up with the volume of reading expected in French academic life. Drop PDFs into the converter on this page, generate audio, and listen on the way to class. The same voices, accents, and behavior are available in the Listening app and the Chrome extension when you are ready for the full workflow.',
  },
];

const faqs = [
  {
    question: 'Which French voices are available?',
    answer:
      'Natural metropolitan French (the voice you hear on Radio France) and Canadian French (Québécois) voices in male and female options. Choose the accent that matches your audience.',
  },
  {
    question: 'How are liaisons and elisions handled?',
    answer:
      'Our French voices produce natural enchaînements, liaisons, and dropped vowels rather than reading word by word. The audio flows the way native French actually sounds.',
  },
  {
    question: 'Is it accurate on French academic vocabulary?',
    answer:
      'Yes. The voices are trained on French academic and literary content, so philosophical, literary, and technical terminology pronounces correctly.',
  },
  {
    question: 'What are the free preview limits?',
    answer:
      'The free preview is capped at 10 pages, 25,000 characters, and 25 MB per PDF. Sign up to convert full French documents with no caps.',
  },
];

export const Route = createFileRoute('/_marketing/_seo/french-pdf-to-audio')({
  validateSearch: pdfToAudioSearch,
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'French PDF to Audio — Listen to French PDFs with Native Voices | Listening',
        description:
          'Convert French PDFs to audio with natural Parisian and Canadian French AI voices. Built for academic papers, literature, and language learning. Free, no sign-up.',
      }),
    ],
    links: [...hreflangLinks('/french-pdf-to-audio')],
  }),
  component: () => (
    <PdfToAudioDemoPage
      converterHeading={heroTitle}
      converterSubtitle={heroDescription}
      faqDescription='Quick answers about converting French PDFs to audio.'
      faqTitle='French PDF to audio FAQ'
      faqs={faqs}
      featureBadge='French voices'
      featureFeatures={featureFeatures}
      featureTitle='the French PDF tool fluent listeners and learners keep:'
      preferredLang='fr'
      seoSections={seoSections}
      solutionBenefitsData={solutionBenefitsData}
      useCasesData={useCasesData}
    />
  ),
});
